import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, FolderKanban, LayoutDashboard, LifeBuoy, Search, WalletCards } from 'lucide-react'
import { useNavigate } from 'react-router'
import { apiGet } from '@/api/axios-client'
import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command'
import { cardsApi } from '@/features/cards/api'
import { usePermission } from '@/features/permissions/usePermission'
import { paths } from '@/routes/paths'
import type { Card, Client, FinanceRequest, Project } from '@/types/entities'

interface SupportSearchRow { id: string; ticketId: string; title: string; description?: string }

function matches(value: string, query: string) {
  const haystack = value.toLocaleLowerCase()
  return query.toLocaleLowerCase().split(/\s+/).filter(Boolean).every((word) => haystack.includes(word))
}

/** Ctrl/Cmd+K search across the primary work objects, without forcing a page change first. */
export function GlobalCommandPalette({ workspaceSlug, workspaceId }: { workspaceSlug: string; workspaceId?: string }) {
  const navigate = useNavigate()
  const { can } = usePermission()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ready = open && query.trim().length >= 2
  const q = query.trim()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) return
      event.preventDefault()
      setOpen((current) => !current)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const workQuery = useQuery({
    queryKey: ['global-search', 'work', workspaceId, q],
    queryFn: () => cardsApi.query({ filter: { workspaceId, text: q, archived: false }, limit: 12 }),
    enabled: ready && Boolean(workspaceId) && can('read', 'Card'),
    staleTime: 30_000,
  })
  const clientsQuery = useQuery({
    queryKey: ['global-search', 'clients'],
    queryFn: () => apiGet<{ items: Client[] }>('clients'),
    enabled: ready && can('read', 'Client'),
    staleTime: 60_000,
  })
  const projectsQuery = useQuery({
    queryKey: ['global-search', 'projects'],
    queryFn: () => apiGet<{ items: Project[] }>('projects'),
    enabled: ready && can('read', 'Project'),
    staleTime: 60_000,
  })
  const financeQuery = useQuery({
    queryKey: ['global-search', 'finance', workspaceId],
    queryFn: () => apiGet<{ items: FinanceRequest[] }>('finance', workspaceId ? { workspaceId } : undefined),
    enabled: ready && Boolean(workspaceId) && can('read', 'FinanceRequest'),
    staleTime: 60_000,
  })
  const supportQuery = useQuery({
    queryKey: ['global-search', 'support', workspaceId],
    // The support API has no text search, so this reads the newest page and filters here.
    // Older tickets are found on the Support page itself.
    queryFn: () => apiGet<{ items: SupportSearchRow[] }>('support-tickets', { page: 1 }),
    enabled: ready && can('read', 'Card'),
    staleTime: 30_000,
  })

  const clients = useMemo(
    () => (clientsQuery.data?.items ?? []).filter((item) => matches(item.name, q)).slice(0, 8),
    [clientsQuery.data, q],
  )
  const projects = useMemo(
    () => (projectsQuery.data?.items ?? []).filter((item) => matches(item.name, q)).slice(0, 8),
    [projectsQuery.data, q],
  )
  const finance = useMemo(
    () => (financeQuery.data?.items ?? []).filter((item) => matches(item.title, q)).slice(0, 8),
    [financeQuery.data, q],
  )
  const support = useMemo(
    () => (supportQuery.data?.items ?? []).filter((item) => matches(`${item.ticketId} ${item.title} ${item.description ?? ''}`, q)).slice(0, 8),
    [supportQuery.data, q],
  )
  const work = workQuery.data?.items ?? []
  const hasAny = work.length + clients.length + projects.length + finance.length + support.length > 0

  const go = (to: string) => {
    setOpen(false)
    setQuery('')
    navigate(to)
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="hidden h-9 w-full min-w-0 justify-between gap-3 rounded-lg bg-card px-3 text-sm font-normal text-text-muted shadow-xs hover:border-brand/40 hover:bg-card sm:flex"
        onClick={() => setOpen(true)}
        aria-label="Search Tapwork"
      >
        <span className="inline-flex min-w-0 items-center gap-2.5"><Search className="size-4 shrink-0" /> <span className="truncate">Search work, clients, projects…</span></span>
        <kbd className="hidden shrink-0 rounded border border-border bg-surface-sunken px-1.5 py-0.5 font-mono text-micro md:inline">⌘K</kbd>
      </Button>
      <Button type="button" variant="ghost" size="icon" className="size-9 sm:hidden" onClick={() => setOpen(true)} aria-label="Search Tapwork">
        <Search className="size-4" />
      </Button>

      <CommandDialog shouldFilter={false} open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery('') }}>
        <CommandInput value={query} onValueChange={setQuery} placeholder="Search work, clients, projects, finance or support…" />
        <CommandList>
          {q.length < 2 ? (
            <div className="px-4 py-8 text-center text-sm text-text-muted">Type at least two characters.</div>
          ) : null}
          {q.length >= 2 && !hasAny && !(workQuery.isFetching || clientsQuery.isFetching || projectsQuery.isFetching || financeQuery.isFetching || supportQuery.isFetching) ? (
            <CommandEmpty>No matching Tapwork records.</CommandEmpty>
          ) : null}
          {work.length ? <CommandGroup heading="Work items">{work.map((item: Card) => (
            <CommandItem key={item.id} value={`work ${item.code ?? ''} ${item.title}`} onSelect={() => go(paths.workItem(workspaceSlug, item.id))}>
              <LayoutDashboard /><span className="truncate">{item.code ? `${item.code} · ` : ''}{item.title}</span>
            </CommandItem>
          ))}</CommandGroup> : null}
          {projects.length ? <CommandGroup heading="Projects">{projects.map((item) => (
            <CommandItem key={item.id} value={`project ${item.name}`} onSelect={() => go(paths.projectDetail(workspaceSlug, item.id))}>
              <FolderKanban /><span className="truncate">{item.name}</span>
            </CommandItem>
          ))}</CommandGroup> : null}
          {clients.length ? <CommandGroup heading="Clients">{clients.map((item) => (
            <CommandItem key={item.id} value={`client ${item.name}`} onSelect={() => go(paths.clientDetail(workspaceSlug, item.id))}>
              <Building2 /><span className="truncate">{item.name}</span>
            </CommandItem>
          ))}</CommandGroup> : null}
          {finance.length ? <CommandGroup heading="Finance">{finance.map((item) => (
            <CommandItem key={item.id} value={`finance ${item.title}`} onSelect={() => go(paths.financeRequest(workspaceSlug, item.id))}>
              <WalletCards /><span className="truncate">{item.title}</span>
            </CommandItem>
          ))}</CommandGroup> : null}
          {support.length ? <CommandGroup heading="Support tickets">{support.map((item) => (
            <CommandItem key={item.id} value={`support ${item.ticketId} ${item.title}`} onSelect={() => go(paths.supportTicket(workspaceSlug, item.id))}>
              <LifeBuoy /><span className="truncate">#{item.ticketId} · {item.title}</span>
            </CommandItem>
          ))}</CommandGroup> : null}
          {q.length < 2 || matches('work projects clients go to', q) ? <CommandGroup heading="Go to">
            <CommandItem value="go work" onSelect={() => go(paths.work(workspaceSlug))}><LayoutDashboard />Work<CommandShortcut>G W</CommandShortcut></CommandItem>
            <CommandItem value="go projects" onSelect={() => go(paths.projects(workspaceSlug))}><FolderKanban />Projects</CommandItem>
            <CommandItem value="go clients" onSelect={() => go(paths.clients(workspaceSlug))}><Building2 />Clients</CommandItem>
          </CommandGroup> : null}
        </CommandList>
      </CommandDialog>
    </>
  )
}
